import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Building2, MapPin, Navigation } from 'lucide-react';
import { api } from '../../api/client';
import { showToast } from '../../components/common/Toast';
import Modal from '../../components/common/Modal';
import GoogleMapPreview from '../../components/common/GoogleMapPreview';
import InteractiveMapPicker from '../../components/admin/InteractiveMapPicker';

export default function AdminOfficesPage() {
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingOffice, setEditingOffice] = useState(null);
  const [selectedMapOffice, setSelectedMapOffice] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    city: 'Jizzax',
    addressUz: '',
    addressRu: '',
    addressEn: '',
    phone: '+998(97) 325-07-70',
    email: 'info@advokatfirm.uz',
    latitude: 40.160331,
    longitude: 67.827246,
    workingHoursText: 'Dushanba - Shanba: 09:00 - 18:00',
    isActive: true,
  });
  const [mapPasteInput, setMapPasteInput] = useState('');

  const handleParseMapInput = () => {
    if (!mapPasteInput.trim()) {
      showToast('Google Maps havolasi yoki koordinatalarini kiriting', 'warning');
      return;
    }

    const str = mapPasteInput.trim();

    // 1. Plain coordinates: "40.1158, 67.8422" or "40.1158 67.8422"
    const plainMatch = str.match(/([-+]?\d{1,2}\.\d+)[,\s]+([-+]?\d{1,3}\.\d+)/);
    if (plainMatch) {
      const lat = parseFloat(plainMatch[1]);
      const lng = parseFloat(plainMatch[2]);
      if (!isNaN(lat) && !isNaN(lng)) {
        setFormData((prev) => ({ ...prev, latitude: lat, longitude: lng }));
        showToast(`Koordinatalar muvaffaqiyatli saqlandi: ${lat.toFixed(5)}, ${lng.toFixed(5)}`, 'success');
        return;
      }
    }

    // 2. Google Maps URL @lat,lng or q=lat,lng or ll=lat,lng
    const urlMatch = str.match(/@([-+]?\d{1,2}\.\d+),([-+]?\d{1,3}\.\d+)/) ||
                     str.match(/[?&]q=([-+]?\d{1,2}\.\d+),([-+]?\d{1,3}\.\d+)/) ||
                     str.match(/[?&]ll=([-+]?\d{1,2}\.\d+),([-+]?\d{1,3}\.\d+)/);
    if (urlMatch) {
      const lat = parseFloat(urlMatch[1]);
      const lng = parseFloat(urlMatch[2]);
      if (!isNaN(lat) && !isNaN(lng)) {
        setFormData((prev) => ({ ...prev, latitude: lat, longitude: lng }));
        showToast(`Google Maps havolasidan koordinatalar olindi: ${lat.toFixed(5)}, ${lng.toFixed(5)}`, 'success');
        return;
      }
    }

    showToast('Koordinatalar formatini aniqlab bo‘lmadi. Masalan: 40.1158, 67.8422', 'error');
  };

  const loadOffices = () => {
    setLoading(true);
    api.get('/offices?includeInactive=true')
      .then((res) => {
        if (res?.success) setOffices(res.offices || []);
      })
      .catch((err) => showToast(err.message, 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadOffices();
  }, []);

  const handleOpenAdd = () => {
    setEditingOffice(null);
    setFormData({
      name: '',
      city: 'Jizzax',
      addressUz: '',
      addressRu: '',
      addressEn: '',
      phone: '+998(97) 325-07-70',
      email: 'info@advokatfirm.uz',
      latitude: 40.160331,
      longitude: 67.827246,
      workingHoursText: 'Dushanba - Shanba: 09:00 - 18:00',
      isActive: true,
    });
    setIsModalOpen(true);
  };

  const handleOpenEdit = (off) => {
    setEditingOffice(off);
    setFormData({
      name: off.name,
      city: off.city,
      addressUz: off.addressUz || off.address || '',
      addressRu: off.addressRu || '',
      addressEn: off.addressEn || '',
      phone: off.phone,
      email: off.email || '',
      latitude: off.latitude,
      longitude: off.longitude,
      workingHoursText: off.workingHoursText || '',
      isActive: off.isActive,
    });
    setIsModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.addressUz) {
      showToast('Ofis nomi va manzili majburiy', 'error');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        city: formData.city.trim(),
        addressUz: formData.addressUz.trim(),
        addressRu: formData.addressRu?.trim() || formData.addressUz.trim(),
        addressEn: formData.addressEn?.trim() || formData.addressUz.trim(),
        phone: formData.phone.trim(),
        email: formData.email?.trim() || 'info@advokatfirm.uz',
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        workingHoursText: formData.workingHoursText?.trim() || 'Dushanba - Shanba: 09:00 - 18:00',
        isActive: Boolean(formData.isActive),
      };

      if (editingOffice) {
        const res = await api.put(`/offices/${editingOffice.id}`, payload);
        if (res?.success) {
          showToast('Ofis ma’lumotlari va koordinatalari yangilandi', 'success');
          setIsModalOpen(false);
          loadOffices();
        }
      } else {
        const res = await api.post('/offices', payload);
        if (res?.success) {
          showToast('Yangi ofis muvaffaqiyatli qo‘shildi', 'success');
          setIsModalOpen(false);
          loadOffices();
        }
      }
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Haqiqatan ham "${name}" ofisini o‘chirmoqchimisiz?`)) return;
    try {
      const res = await api.delete(`/offices/${id}`);
      if (res?.success) {
        showToast(res.message || 'Ofis holati yangilandi', 'success');
        loadOffices();
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-lg font-bold text-slate-900">
            Ofislar va Filiallar ({offices.length})
          </h2>
          <p className="text-xs text-slate-500">
            Geografik lokatsiyalar, Google Maps koordinatalari va ofis jadvali
          </p>
        </div>

        <button
          onClick={handleOpenAdd}
          className="inline-flex items-center px-4 py-2 text-xs font-semibold text-[#091322] bg-[#c5a880] hover:bg-[#d4b993] rounded shadow-sm transition-colors"
        >
          <Plus className="w-4 h-4 mr-1.5" />
          Yangi ofis qo‘shish
        </button>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 shadow-2xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="py-3 px-4">Ofis Nomi</th>
                <th className="py-3 px-4">Shahar</th>
                <th className="py-3 px-4">Manzil</th>
                <th className="py-3 px-4">Telefon</th>
                <th className="py-3 px-4">Koordinatalar</th>
                <th className="py-3 px-4">Holat</th>
                <th className="py-3 px-4 text-right">Amallar</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-slate-400">Yuklanmoqda...</td>
                </tr>
              ) : (
                offices.map((o) => (
                  <tr key={o.id} className="hover:bg-slate-50 transition-colors">
                    <td className="py-3 px-4 font-semibold text-slate-900">
                      {o.name}
                    </td>
                    <td className="py-3 px-4 text-slate-700">
                      {o.city}
                    </td>
                    <td className="py-3 px-4 text-slate-600 max-w-xs truncate">
                      {o.addressUz}
                    </td>
                    <td className="py-3 px-4 font-mono text-slate-700">
                      {o.phone}
                    </td>
                    <td className="py-3 px-4 font-mono text-slate-500 text-[11px]">
                      {o.latitude.toFixed(4)}, {o.longitude.toFixed(4)}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border ${o.isActive ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                        {o.isActive ? 'Faol' : 'Nofaol'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="inline-flex space-x-2">
                        <button
                          onClick={() => setSelectedMapOffice(o)}
                          className="p-1 text-[#c5a880] hover:text-[#b38038] hover:bg-amber-50 rounded"
                          title="Xaritada ko‘rish"
                        >
                          <MapPin className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleOpenEdit(o)}
                          className="p-1 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded"
                          title="Tahrirlash"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(o.id, o.name)}
                          className="p-1 text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded"
                          title="O‘chirish"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingOffice ? `Ofisni Tahrirlash: ${editingOffice.name}` : 'Yangi Ofis Qo‘shish'}
        maxWidth="max-w-3xl"
      >
        <form onSubmit={handleSave} className="space-y-4 text-xs">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block font-semibold text-slate-700 uppercase mb-1">Ofis Nomi *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border rounded focus:border-[#c5a880]"
                required
              />
            </div>

            <div>
              <label className="block font-semibold text-slate-700 uppercase mb-1">Shahar *</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="w-full px-3 py-2 border rounded focus:border-[#c5a880]"
                required
              />
            </div>

            <div className="sm:col-span-2">
              <label className="block font-semibold text-slate-700 uppercase mb-1">Manzil (O‘zbekcha) *</label>
              <input
                type="text"
                value={formData.addressUz}
                onChange={(e) => setFormData({ ...formData, addressUz: e.target.value })}
                className="w-full px-3 py-2 border rounded focus:border-[#c5a880]"
                required
              />
            </div>

            <div>
              <label className="block font-semibold text-slate-700 uppercase mb-1">Telefon *</label>
              <input
                type="text"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-3 py-2 border rounded focus:border-[#c5a880]"
                required
              />
            </div>

            <div>
              <label className="block font-semibold text-slate-700 uppercase mb-1">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-3 py-2 border rounded focus:border-[#c5a880]"
              />
            </div>

            {/* Location Quick Presets */}
            <div className="sm:col-span-2 bg-amber-50/70 border border-amber-200 p-3 rounded-lg text-xs">
              <div className="font-semibold text-amber-950 mb-1.5 flex items-center">
                <MapPin className="w-3.5 h-3.5 mr-1 text-[#b38038]" />
                Tezkor lokatsiya va Google Maps koordinatalari:
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setFormData((prev) => ({
                    ...prev,
                    city: 'Jizzax',
                    name: prev.name || 'Jizzax Bosh Ofis',
                    addressUz: "Jizzax shahar, Islom Karimov ko'chasi, Turon Bank ro'parasi",
                    latitude: 40.160331,
                    longitude: 67.827246,
                  }))}
                  className="inline-flex items-center px-2.5 py-1 bg-white border border-slate-300 rounded text-[11px] font-medium text-slate-800 hover:bg-slate-100 transition-colors"
                >
                  <Building2 className="w-3.5 h-3.5 mr-1 text-[#091322]" />
                  Jizzax Bosh Ofis (Turon Bank ro'parasi)
                </button>
                <button
                  type="button"
                  onClick={() => setFormData((prev) => ({
                    ...prev,
                    city: 'Toshkent',
                    name: prev.name || 'Toshkent Markaziy Filiali',
                    addressUz: "Toshkent shahri, Mirobod tumani, Amir Temur shox ko'chasi, 14-uy",
                    latitude: 41.311081,
                    longitude: 69.279737,
                  }))}
                  className="inline-flex items-center px-2.5 py-1 bg-white border border-slate-300 rounded text-[11px] font-medium text-slate-800 hover:bg-slate-100 transition-colors"
                >
                  <Building2 className="w-3.5 h-3.5 mr-1 text-[#091322]" />
                  Toshkent Filiali
                </button>
                <button
                  type="button"
                  onClick={() => setFormData((prev) => ({
                    ...prev,
                    city: 'Samarqand',
                    name: prev.name || 'Samarqand Filiali',
                    addressUz: "Samarqand shahri, Registon ko'chasi, 22-uy",
                    latitude: 39.6542,
                    longitude: 66.9597,
                  }))}
                  className="inline-flex items-center px-2.5 py-1 bg-white border border-slate-300 rounded text-[11px] font-medium text-slate-800 hover:bg-slate-100 transition-colors"
                >
                  <Building2 className="w-3.5 h-3.5 mr-1 text-[#091322]" />
                  Samarqand Filiali
                </button>
              </div>
            </div>

            {/* Google Maps Smart Link / Coords Extractor */}
            <div className="sm:col-span-2 bg-slate-50 border border-slate-300 p-3 rounded-lg text-xs space-y-2">
              <label className="block font-semibold text-slate-800">
                Google Maps Havolasi yoki Koordinatasidan Avtomatik Olish:
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Google Maps linki yoki koordinata (masalan: 40.1158, 67.8422 yoki maps.google.com link)"
                  value={mapPasteInput}
                  onChange={(e) => setMapPasteInput(e.target.value)}
                  className="flex-1 px-3 py-2 border border-slate-300 rounded text-xs bg-white focus:border-[#c5a880]"
                />
                <button
                  type="button"
                  onClick={handleParseMapInput}
                  className="px-4 py-2 bg-[#091322] text-[#c5a880] rounded text-xs font-semibold cursor-pointer hover:bg-[#15253e] shrink-0"
                >
                  Aniqlash & Belgilash
                </button>
              </div>
              <p className="text-[11px] text-slate-500">
                * Google Maps dasturi yoki brauzerdagi xaritadan nusxalab tashlang — Kenglik va Uzunlik avtomatik to‘ldiriladi.
              </p>
            </div>

            <div>
              <label className="block font-semibold text-slate-700 uppercase mb-1">Latitude (Kenglik) *</label>
              <input
                type="number"
                step="any"
                value={formData.latitude}
                onChange={(e) => setFormData({ ...formData, latitude: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 border rounded focus:border-[#c5a880] font-mono"
                required
              />
            </div>

            <div>
              <label className="block font-semibold text-slate-700 uppercase mb-1">Longitude (Uzunlik) *</label>
              <input
                type="number"
                step="any"
                value={formData.longitude}
                onChange={(e) => setFormData({ ...formData, longitude: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 border rounded focus:border-[#c5a880] font-mono"
                required
              />
            </div>

            {/* Interactive Live Map Picker with Search & Layer Toggle */}
            <div className="sm:col-span-2">
              <InteractiveMapPicker
                latitude={formData.latitude}
                longitude={formData.longitude}
                height="320px"
                onChange={({ latitude, longitude }) => {
                  setFormData((prev) => ({ ...prev, latitude, longitude }));
                }}
              />
            </div>

            <div className="sm:col-span-2">
              <label className="block font-semibold text-slate-700 uppercase mb-1">Ish tartibi matni</label>
              <input
                type="text"
                value={formData.workingHoursText}
                onChange={(e) => setFormData({ ...formData, workingHoursText: e.target.value })}
                className="w-full px-3 py-2 border rounded focus:border-[#c5a880]"
              />
            </div>

            <div className="flex items-center space-x-2 pt-2 sm:col-span-2">
              <input
                type="checkbox"
                id="off-active"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="rounded border-slate-300 text-[#091322]"
              />
              <label htmlFor="off-active" className="font-semibold text-slate-800">
                Ofis saytda faol ko‘rinsin
              </label>
            </div>
          </div>

          <div className="pt-4 border-t flex justify-end space-x-2">
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="px-4 py-2 border rounded text-slate-700 hover:bg-slate-50"
            >
              Bekor qilish
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-[#091322] text-[#c5a880] font-semibold rounded hover:bg-[#11223b]"
            >
              {saving ? 'Saqlanmoqda...' : 'Saqlash'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Map Preview Modal */}
      <Modal
        isOpen={!!selectedMapOffice}
        onClose={() => setSelectedMapOffice(null)}
        title={selectedMapOffice ? `Xarita va Geolokatsiya: ${selectedMapOffice.name}` : ''}
        maxWidth="max-w-3xl"
      >
        {selectedMapOffice && (
          <GoogleMapPreview office={selectedMapOffice} className="w-full min-h-[400px]" />
        )}
      </Modal>
    </div>
  );
}
